import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  loading: true,
  refreshAuth: () => {},
  logout: () => {},
});

// Helper: get auth headers from localStorage token
function getAuthHeaders() {
  const token = localStorage.getItem('hustlehub_session');
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setUser(data);
    } catch {
      // Token might be expired — clear it
      localStorage.removeItem('hustlehub_session');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
    } catch { /* ignore */ }
    localStorage.removeItem('hustlehub_session');
    setUser(null);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refreshAuth();
  }, [refreshAuth]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      loading,
      refreshAuth,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
